import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import useParkingService from '../../../hooks/parking-services/useParkingService.ts';
import useBrand from '../../../hooks/brands/useBrand.ts';
import React from 'react';
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Group,
  LoadingOverlay,
  Paper,
  Popover,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { buildStoreUrl } from '../../../utils/storeUrl.ts';
import {
  IconBrowser,
  IconCar,
  IconClock,
  IconCreditCard,
  IconEdit,
  IconParking,
  IconPhoto,
  IconTrash,
  IconZoomMoney,
} from '@tabler/icons-react';
import {
  getServiceStatusColor,
  getServiceStatusLabel,
} from '../../../const/service_status_mapping.ts';
import { modals } from '@mantine/modals';
import { dayMap } from '../../../utils/DateUtil.ts';
import { useQueries } from '@tanstack/react-query';
import { getTag } from '../../../api/tag.ts';
import { getPaymentMethod } from '../../../api/payment-method.ts';
import { ServiceType } from '../../../models/ServiceType.ts';
import {
  buildingTypeMap,
  mechanicalSpacesMap,
  parkingLotTypeMap,
} from '../../../const/parking_service_enum_mapping.ts';
import { useTheme } from '../../../context/ThemeProvider.tsx';

const phoneUtil = PhoneNumberUtil.getInstance();

interface ParkingServiceInformationCardProps {
  serviceId: string;
  setIsServiceImageCarouselOpen: (isServiceImageCarouselOpen: boolean) => void;
  setSelectedServiceIdForCarousel: (
    selectedServiceIdForCarousel: string | null,
  ) => void;
  setSelectedServiceTypeForCarousel: (
    selectedServiceTypeForCarousel: ServiceType | null,
  ) => void;
  handleEditParkingServiceMetadataClick: (serviceId: string) => void;
  handleUpdateParkingServiceParkingInfoClick: (serviceId: string) => void;
  handleDeleteParkingServiceClick: (serviceId: string) => void;
  handleUpdateParkingServiceHeaderInfoClick: (serviceId: string) => void;
  handleUpdateParkingServiceParkingRateClick: (serviceId: string) => void;
}

const ExistingServicesInfo = [
  {
    key: 'hasRestroom',
    label: '廁所',
    getExists: (s) => s.hasRestroom === 'EXISTS',
  },
  {
    key: 'hasElevator',
    label: '電梯',
    getExists: (s) => s.hasElevator === 'EXISTS',
  },
  {
    key: 'hasParkingBooth',
    label: '管理室',
    getExists: (s) => s.hasParkingBooth === 'EXISTS',
  },
];

const ParkingServiceInformationCard: React.FC<
  ParkingServiceInformationCardProps
> = ({
  serviceId,
  setIsServiceImageCarouselOpen,
  setSelectedServiceIdForCarousel,
  setSelectedServiceTypeForCarousel,
  handleEditParkingServiceMetadataClick,
  handleUpdateParkingServiceParkingInfoClick,
  handleDeleteParkingServiceClick,
  handleUpdateParkingServiceHeaderInfoClick,
  handleUpdateParkingServiceParkingRateClick,
}) => {
  const { data: service, isLoading: isParkingServiceLoading } =
    useParkingService(serviceId);
  const { data: brand, isLoading: isBrandLoading } = useBrand(service?.brandId);

  const tagQueries = useQueries({
    queries: (service?.tags ?? []).map((tagId) => ({
      queryKey: ['tags', tagId],
      queryFn: () => getTag(tagId),
      enabled: !!tagId,
    })),
  });
  const tagNames = tagQueries.map((q) => q.data?.name).filter(Boolean);
  const isTagsLoading = tagQueries.some((q) => q.isLoading);

  const paymentMethodQueries = useQueries({
    queries: (service?.paymentMethods ?? []).map((paymentMethodId) => ({
      queryKey: ['paymentMethods', paymentMethodId],
      queryFn: () => getPaymentMethod(paymentMethodId),
      enabled: !!paymentMethodId,
    })),
  });
  const paymentMethodNames = paymentMethodQueries
    .map((q) => q.data?.name)
    .filter(Boolean);
  const isPaymentMethodsLoading = paymentMethodQueries.some((q) => q.isLoading);

  const { colorScheme } = useTheme();

  if (
    isBrandLoading ||
    isParkingServiceLoading ||
    isTagsLoading ||
    isPaymentMethodsLoading
  )
    return <LoadingOverlay />;

  const existingServices = ExistingServicesInfo.filter(({ getExists }) =>
    getExists(service),
  );

  return (
    <>
      <Paper
        key={serviceId}
        shadow={'xs'}
        style={{ height: 588, borderRadius: 16, marginBottom: 20 }}
      >
        <Group
          style={{
            justifyContent: 'space-between',
            paddingInline: 30,
            paddingTop: 38,
            paddingBottom: 19,
          }}
        >
          <Group gap={18}>
            <ThemeIcon
              style={{
                width: 62,
                height: 62,
                backgroundColor: '#339AF0',
                borderRadius: '50%',
              }}
            >
              <IconParking style={{ width: 40, height: 40 }} />
            </ThemeIcon>

            <Stack gap={10}>
              <Group>
                <Text
                  style={{
                    lineHeight: '32px',
                    fontSize: 24,
                    fontWeight: 700,
                  }}
                >
                  {service.name}
                </Text>
                <ActionIcon size={'sm'} variant={'light'}>
                  <IconEdit
                    style={{ width: 20, height: 20 }}
                    onClick={() => {
                      handleUpdateParkingServiceHeaderInfoClick(service.id);
                    }}
                  />
                </ActionIcon>
              </Group>

              <Group gap={10}>
                {/* 服務狀態 */}
                <Badge color={getServiceStatusColor(service.serviceStatus)}>
                  {getServiceStatusLabel(service.serviceStatus)}
                </Badge>
                {/* 標籤 */}
                {tagNames.map((tag) => (
                  <Badge variant={'outline'}>{tag}</Badge>
                ))}
              </Group>
            </Stack>
          </Group>

          <ActionIcon size={'lg'} color={'red'} variant={'light'}>
            <IconTrash
              style={{ width: 20, height: 20 }}
              onClick={() =>
                modals.openConfirmModal({
                  title: (
                    <Text
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                      }}
                    >
                      刪除停車服務
                    </Text>
                  ),
                  children: (
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                      }}
                    >
                      是否確認刪除停車服務？
                    </Text>
                  ),
                  centered: true,
                  labels: {
                    confirm: '確認',
                    cancel: '取消',
                  },
                  confirmProps: { color: 'blue' },

                  onConfirm: async () => {
                    handleDeleteParkingServiceClick(serviceId);
                  },
                })
              }
            />
          </ActionIcon>
        </Group>

        <div
          style={{
            height: 1,
            backgroundColor: colorScheme === 'light' ? '#dee2e6' : '#737373ff',
            marginInline: 30,
          }}
        />

        <div
          style={{
            position: 'relative',
            display: 'flex',
            height: 410,
            paddingInline: 30,
            marginTop: 20,
          }}
        >
          {/* 左半邊 */}
          <div style={{ flex: 1, height: 410, marginRight: 40, minWidth: 0 }}>
            <Group
              justify={'space-between'}
              style={{ height: 40, marginBottom: 22 }}
            >
              <Text style={{ fontSize: 20, fontWeight: 400 }}>服務資訊</Text>
              <Button
                variant={'light'}
                leftSection={<IconEdit />}
                onClick={() => handleEditParkingServiceMetadataClick(serviceId)}
              >
                編輯
              </Button>
            </Group>

            {/* 服務圖片按鈕 */}
            <Group gap={16} style={{ marginBottom: 20 }}>
              <Button
                onClick={() => {
                  setSelectedServiceIdForCarousel(serviceId);
                  setSelectedServiceTypeForCarousel('PARKING');
                  setIsServiceImageCarouselOpen(true);
                }}
                disabled={service.imageIds.length === 0}
                variant={'outline'}
                leftSection={<IconPhoto style={{ width: 16, height: 16 }} />}
                radius={'xl'}
                style={{
                  width: 112,
                  height: 32,
                  fontSize: 14,
                  fontWeight: 400,
                }}
                styles={{ section: { marginRight: 8 } }}
              >
                服務圖片
              </Button>

              {/* 官方網站按鈕 */}
              <Button
                component={'a'}
                href={service.website}
                disabled={service.website === ''}
                target={'_blank'}
                variant={'outline'}
                leftSection={<IconBrowser style={{ width: 16, height: 16 }} />}
                radius={'xl'}
                style={{
                  width: 112,
                  height: 32,
                  fontSize: 14,
                  fontWeight: 400,
                }}
                styles={{ section: { marginRight: 8 } }}
              >
                官方網站
              </Button>

              {/* 營業時間按鈕 */}
              <Popover
                withArrow
                styles={{
                  dropdown: {
                    width: 158,
                    height: 160,
                    fontSize: 12,
                    lineHeight: '14px',
                  },
                }}
              >
                <Popover.Target>
                  <Button
                    variant={'outline'}
                    leftSection={
                      <IconClock style={{ width: 16, height: 16 }} />
                    }
                    radius={'xl'}
                    style={{
                      width: 112,
                      height: 32,
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                    styles={{ section: { marginRight: 8 } }}
                  >
                    營業時間
                  </Button>
                </Popover.Target>
                <Popover.Dropdown>
                  <div>
                    <Stack gap={6}>
                      {Object.entries(dayMap).map(([key, { label }]) => {
                        const schedule =
                          service.operationHours?.weeklySchedule ?? [];
                        const day = schedule.find((w) => w.dayOfWeek === key);
                        const isUnknown = day?.isOpen === 'UNKNOWN';
                        const isOpen = day?.isOpen === 'OPEN';

                        const start =
                          day?.timeSlots?.[0]?.startTime
                            ?.split(':')
                            .slice(0, 2)
                            .join(':') ?? '--:--';

                        const end =
                          day?.timeSlots?.[0]?.endTime
                            ?.split(':')
                            .slice(0, 2)
                            .join(':') ?? '--:--';

                        return (
                          <Group key={key} gap={3}>
                            <div>{label}</div>

                            {isOpen ? (
                              <>
                                <div>{start}</div>
                                <div>-</div>
                                <div>{end}</div>
                              </>
                            ) : isUnknown ? (
                              <div style={{ color: 'grey' }}>未知</div>
                            ) : (
                              <div style={{ color: 'grey' }}>休息</div>
                            )}
                          </Group>
                        );
                      })}
                    </Stack>
                  </div>
                </Popover.Dropdown>
              </Popover>

              {/* 可用付款方式按鈕 */}
              <Popover
                withArrow
                styles={{
                  dropdown: {
                    fontSize: 12,
                    lineHeight: '14px',
                  },
                }}
              >
                <Popover.Target>
                  <Button
                    variant={'outline'}
                    leftSection={
                      <IconCreditCard style={{ width: 16, height: 16 }} />
                    }
                    disabled={service.paymentMethods.length === 0}
                    radius={'xl'}
                    style={{
                      width: 140,
                      height: 32,
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                    styles={{ section: { marginRight: 8 } }}
                  >
                    可用付款方式
                  </Button>
                </Popover.Target>
                <Popover.Dropdown>
                  <div>
                    <Stack gap={6} align={'start'}>
                      {paymentMethodNames.map((paymentMethod) => {
                        return (
                          <Text fz={12} fw={400}>
                            {paymentMethod}
                          </Text>
                        );
                      })}
                    </Stack>
                  </div>
                </Popover.Dropdown>
              </Popover>
            </Group>

            <Stack gap={10} style={{ minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  height: 92,
                }}
              >
                {/* 品牌名稱 */}
                <div
                  style={{
                    flex: 1,
                    backgroundColor:
                      colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                    height: '100%',
                    borderRadius: 12,
                  }}
                >
                  <Stack
                    gap={8}
                    style={{
                      paddingInline: 24,
                      paddingTop: 16,
                      paddingBottom: 16,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: '#868E96',
                      }}
                    >
                      品牌名稱
                    </Text>
                    <Text style={{ fontSize: 18, fontWeight: 400 }}>
                      {brand?.name ?? '-'}
                    </Text>
                  </Stack>
                </div>

                {/* 服務電話 */}
                <div
                  style={{
                    flex: 1,
                    backgroundColor:
                      colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                    height: '100%',
                    borderRadius: 12,
                  }}
                >
                  <Stack
                    gap={8}
                    style={{
                      paddingInline: 24,
                      paddingTop: 16,
                      paddingBottom: 16,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: '#868E96',
                      }}
                    >
                      服務電話
                    </Text>
                    <Text style={{ fontSize: 18, fontWeight: 400 }}>
                      {service.phoneNumber
                        ? (() => {
                            try {
                              const parsed = phoneUtil.parse(
                                `${service.phoneNumber.countryCode}${service.phoneNumber.number}`,
                              );

                              return (
                                '(' +
                                service.phoneNumber.countryCode +
                                ') ' +
                                phoneUtil.format(
                                  parsed,
                                  PhoneNumberFormat.NATIONAL,
                                )
                              );
                            } catch {
                              return '-';
                            }
                          })()
                        : '-'}
                    </Text>
                  </Stack>
                </div>
              </div>

              {/* 營運商店 */}
              <div
                style={{
                  display: 'flex',
                  height: 92,
                  backgroundColor:
                    colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                  borderRadius: 12,
                }}
              >
                <Stack
                  gap={8}
                  style={{
                    paddingInline: 24,
                    paddingTop: 16,
                    paddingBottom: 16,
                  }}
                >
                  <Group gap={8}>
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: '#868E96',
                      }}
                    >
                      營運商店
                    </Text>
                  </Group>
                  {service.storeIds.length === 0 ? (
                    <Text
                      style={{
                        fontSize: 18,
                        fontWeight: 400,
                      }}
                    >
                      -
                    </Text>
                  ) : (
                    <Group gap={12}>
                      {service.storeIds.map((entry, index) => (
                        <React.Fragment key={`${entry.type}-${entry.value}`}>
                          {index > 0 && (
                            <Text style={{ fontSize: 18, fontWeight: 400 }}>
                              |
                            </Text>
                          )}
                          <Anchor
                            href={buildStoreUrl(entry)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 18, fontWeight: 400 }}
                          >
                            {'前往商店 - '}
                            {entry.type === 'PARKINGLOT'
                              ? '停車場'
                              : entry.type === 'GAS_STATION'
                                ? '加油站'
                                : '連鎖店'}
                          </Anchor>
                        </React.Fragment>
                      ))}
                    </Group>
                  )}
                </Stack>
              </div>

              {/* 備註文字 */}
              <div
                style={{
                  display: 'flex',
                  height: 92,
                  minWidth: 0,
                  backgroundColor:
                    colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                  borderRadius: 12,
                  overflow: 'hidden',
                }}
              >
                <Stack
                  gap={8}
                  style={{
                    paddingInline: 24,
                    paddingTop: 16,
                    paddingBottom: 16,
                    flex: 1,
                    minWidth: 0,
                    maxWidth: 758,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 14,
                      fontWeight: 400,
                      color: '#868E96',
                    }}
                  >
                    備註文字
                  </Text>
                  <Tooltip
                    multiline
                    position="top-start"
                    label={
                      <Text style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>
                        {service.notes}
                      </Text>
                    }
                    disabled={!service.notes}
                    styles={{
                      tooltip: {
                        maxWidth: 300,
                      },
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 18,
                        fontWeight: 400,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {service.notes
                        ? service.notes.split('\n').length > 1
                          ? `${service.notes.split('\n')[0]} ...`
                          : service.notes
                        : '-'}
                    </Text>
                  </Tooltip>
                </Stack>
              </div>
            </Stack>
          </div>

          {/* 右半邊 */}
          <div style={{ flex: 1, height: 410 }}>
            <Group
              justify={'space-between'}
              style={{ height: 40, marginBottom: 22 }}
            >
              <Text style={{ fontSize: 20, fontWeight: 400 }}>停車資訊</Text>
              <Button
                variant={'light'}
                leftSection={<IconEdit />}
                onClick={() =>
                  handleUpdateParkingServiceParkingInfoClick(serviceId)
                }
              >
                編輯
              </Button>
            </Group>

            <Group gap={16} style={{ marginBottom: 20 }}>
              <Popover
                withArrow
                styles={{
                  dropdown: {
                    height: 168,
                    fontSize: 12,
                    lineHeight: '16px',
                  },
                }}
              >
                <Popover.Target>
                  <Button
                    variant={'outline'}
                    leftSection={<IconCar style={{ width: 16, height: 16 }} />}
                    radius={'xl'}
                    style={{
                      width: 112,
                      height: 32,
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                    styles={{ section: { marginRight: 8 } }}
                  >
                    車位數量
                  </Button>
                </Popover.Target>
                <Popover.Dropdown>
                  <div>
                    <Stack gap={0} align={'start'}>
                      <Group h={16} w={166} gap={0}>
                        <Text fz={12} fw={700}>
                          即時總車位數：
                        </Text>
                        <Text fz={12} fw={400}>
                          {service.parkingSpaceInfo.availableSpacesSum ??
                            '未知'}
                        </Text>
                      </Group>
                      <Group h={16} w={166} gap={0}>
                        <Text fz={12} fw={700}>
                          總車位數：
                        </Text>
                        <Text fz={12} fw={400}>
                          {service.parkingSpaceInfo.totalSpacesSum ?? '未知'}
                        </Text>
                      </Group>
                      <Group h={16} w={166} gap={0}>
                        <Text fz={12} fw={700}>
                          即時自小客車位數：
                        </Text>
                        <Text fz={12} fw={400}>
                          {service.parkingSpaceInfo.availableCarSpaces ??
                            '未知'}
                        </Text>
                      </Group>
                      <Group h={16} w={166} gap={0}>
                        <Text fz={12} fw={700}>
                          自小客車位數：
                        </Text>
                        <Text fz={12} fw={400}>
                          {service.parkingSpaceInfo.totalCarSpaces ?? '未知'}
                        </Text>
                      </Group>
                      <Group h={16} w={166} gap={0}>
                        <Text fz={12} fw={700}>
                          即時普通重型機車位數：
                        </Text>
                        <Text fz={12} fw={400}>
                          {service.parkingSpaceInfo.availableMotoSpaces ??
                            '未知'}
                        </Text>
                      </Group>
                      <Group h={16} w={166} gap={0}>
                        <Text fz={12} fw={700}>
                          普通重型機車位數：
                        </Text>
                        <Text fz={12} fw={400}>
                          {service.parkingSpaceInfo.totalMotoSpaces ?? '未知'}
                        </Text>
                      </Group>
                      <Group h={16} w={166} gap={0}>
                        <Text fz={12} fw={700}>
                          大型重型機車位數：
                        </Text>
                        <Text fz={12} fw={400}>
                          {service.parkingSpaceInfo.totalHeavyMotoSpaces ??
                            '未知'}
                        </Text>
                      </Group>
                      <Group h={16} w={166} gap={0}>
                        <Text fz={12} fw={700}>
                          身心障礙車位數：
                        </Text>
                        <Text fz={12} fw={400}>
                          {service.parkingSpaceInfo
                            .totalDisabledParkingSpaces ?? '未知'}
                        </Text>
                      </Group>
                      <Group h={16} w={166} gap={0}>
                        <Text fz={12} fw={700}>
                          孕婦／親子車位數：
                        </Text>
                        <Text fz={12} fw={400}>
                          {service.parkingSpaceInfo
                            .totalMaternityParkingSpaces ?? '未知'}
                        </Text>
                      </Group>
                    </Stack>
                  </div>
                </Popover.Dropdown>
              </Popover>
              <Button
                variant={'outline'}
                leftSection={
                  <IconZoomMoney style={{ width: 16, height: 16 }} />
                }
                onClick={() =>
                  handleUpdateParkingServiceParkingRateClick(serviceId)
                }
                radius={'xl'}
                style={{
                  width: 84,
                  height: 32,
                  fontSize: 14,
                  fontWeight: 400,
                }}
                styles={{ section: { marginRight: 8 } }}
              >
                費率
              </Button>
            </Group>

            <Stack gap={10}>
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  height: 92,
                }}
              >
                <div
                  style={{
                    flex: 1,
                    backgroundColor:
                      colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                    height: '100%',
                    borderRadius: 12,
                  }}
                >
                  {/* 機械車位 */}
                  <Stack
                    gap={8}
                    style={{
                      paddingInline: 24,
                      paddingTop: 16,
                      paddingBottom: 16,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: '#868E96',
                      }}
                    >
                      機械車位
                    </Text>
                    <Text style={{ fontSize: 18, fontWeight: 400 }}>
                      {mechanicalSpacesMap[service.mechanicalSpaces]}
                    </Text>
                  </Stack>
                </div>

                {/* 室內外狀態 */}
                <div
                  style={{
                    flex: 1,
                    backgroundColor:
                      colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                    height: '100%',
                    borderRadius: 12,
                    overflow: 'hidden',
                  }}
                >
                  <Stack
                    gap={8}
                    style={{
                      paddingInline: 24,
                      paddingTop: 16,
                      paddingBottom: 16,
                      minWidth: 0,
                      width: '100%',
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: '#868E96',
                      }}
                    >
                      室內外狀態
                    </Text>
                    <Tooltip
                      multiline
                      label={
                        <Text style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>
                          {service.buildingTypes.length === 0
                            ? '未知'
                            : [...service.buildingTypes]
                                .sort(
                                  (a, b) =>
                                    buildingTypeMap[a].order -
                                    buildingTypeMap[b].order,
                                )
                                .map(
                                  (buildingType) =>
                                    buildingTypeMap[buildingType].label,
                                )
                                .join(' | ')}
                        </Text>
                      }
                      disabled={!service.buildingTypes}
                      styles={{
                        tooltip: {
                          maxWidth: 300,
                          minWidth: 200,
                        },
                      }}
                    >
                      <Text
                        style={{
                          fontSize: 18,
                          fontWeight: 400,
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {service.buildingTypes.length === 0
                          ? '未知'
                          : [...service.buildingTypes]
                              .sort(
                                (a, b) =>
                                  buildingTypeMap[a].order -
                                  buildingTypeMap[b].order,
                              )
                              .map(
                                (buildingType) =>
                                  buildingTypeMap[buildingType].label,
                              )
                              .join(' | ')}
                      </Text>
                    </Tooltip>
                  </Stack>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  height: 92,
                }}
              >
                <div
                  style={{
                    flex: 1,
                    backgroundColor:
                      colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                    height: '100%',
                    borderRadius: 12,
                  }}
                >
                  {/* 停車場類型 */}
                  <Stack
                    gap={8}
                    style={{
                      paddingInline: 24,
                      paddingTop: 16,
                      paddingBottom: 16,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: '#868E96',
                      }}
                    >
                      停車場類型
                    </Text>
                    <Text style={{ fontSize: 18, fontWeight: 400 }}>
                      {service.parkingLotTypes.length === 0
                        ? '未知'
                        : [...service.parkingLotTypes]
                            .sort(
                              (a, b) =>
                                parkingLotTypeMap[a].order -
                                parkingLotTypeMap[b].order,
                            )
                            .map(
                              (parkingLotType) =>
                                parkingLotTypeMap[parkingLotType].label,
                            )
                            .join(' | ')}
                    </Text>
                  </Stack>
                </div>

                {/* 周邊服務 */}
                <div
                  style={{
                    flex: 1,
                    backgroundColor:
                      colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                    height: '100%',
                    borderRadius: 12,
                  }}
                >
                  <Stack
                    gap={8}
                    style={{
                      paddingInline: 24,
                      paddingTop: 16,
                      paddingBottom: 16,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: '#868E96',
                      }}
                    >
                      周邊服務
                    </Text>
                    <Group gap={4}>
                      {existingServices.length === 0 ? (
                        <Text style={{ fontSize: 18, fontWeight: 400 }}>
                          未知
                        </Text>
                      ) : (
                        existingServices.map(({ key, label }, index) => {
                          return (
                            <React.Fragment key={key}>
                              {index !== 0 && (
                                <Text style={{ fontSize: 18, fontWeight: 400 }}>
                                  {'|'}
                                </Text>
                              )}
                              <Text style={{ fontSize: 18, fontWeight: 400 }}>
                                {label}
                              </Text>
                            </React.Fragment>
                          );
                        })
                      )}
                    </Group>
                  </Stack>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  height: 92,
                }}
              >
                <div
                  style={{
                    flex: 1,
                    backgroundColor:
                      colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                    height: '100%',
                    borderRadius: 12,
                    overflow: 'hidden',
                    minWidth: 0,
                  }}
                >
                  {/* 車位樓層 */}
                  <Stack
                    gap={8}
                    style={{
                      paddingInline: 24,
                      paddingTop: 16,
                      paddingBottom: 16,
                      minWidth: 0,
                      width: '100%',
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: '#868E96',
                      }}
                    >
                      車位樓層
                    </Text>
                    <Tooltip
                      multiline
                      label={
                        <Text style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>
                          {service.floorLevels.length === 0
                            ? '未知'
                            : [...service.floorLevels].join(' | ')}
                        </Text>
                      }
                      disabled={!service.floorLevels}
                      styles={{
                        tooltip: {
                          maxWidth: 300,
                          minWidth: 200,
                        },
                      }}
                    >
                      <Text
                        style={{
                          fontSize: 18,
                          fontWeight: 400,
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {service.floorLevels.length === 0
                          ? '未知'
                          : [...service.floorLevels].join(' | ')}
                      </Text>
                    </Tooltip>
                  </Stack>
                </div>

                {/* 限高 */}
                <div
                  style={{
                    flex: 1,
                    backgroundColor:
                      colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                    height: '100%',
                    borderRadius: 12,
                  }}
                >
                  <Stack
                    gap={8}
                    style={{
                      paddingInline: 24,
                      paddingTop: 16,
                      paddingBottom: 16,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: '#868E96',
                      }}
                    >
                      限高
                    </Text>
                    <Text style={{ fontSize: 18, fontWeight: 400 }}>
                      {service.heightLimit === -1
                        ? '不限'
                        : service.heightLimit === null
                          ? '未知'
                          : `${service.heightLimit} cm`}
                    </Text>
                  </Stack>
                </div>
              </div>
            </Stack>
          </div>

          {/* 中間分隔線 */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: '50%',
              width: 1,
              backgroundColor:
                colorScheme === 'light' ? '#dee2e6' : '#737373ff',
              transform: 'translateX(-0.5px)',
            }}
          />
        </div>
      </Paper>
    </>
  );
};

export default ParkingServiceInformationCard;
